import { setStorage, getFromStorage, flushStorage } from '../helpers/storageHelper';
import { normalizeProfileImageJob } from '../helpers/normalizeProfileImageJob';
import apiClient from '../Interceptors/ApiClient';
import { buildJobMatchQueryParams } from './helpers/buildJobMatchQueryParams';

export const getClientProfile = async () => {
  try {
    const response = await apiClient.get('api/profile');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching client profile:', error);
    throw error;
  }
};

export const getProfileIsCompleted = async () => {
  const cached = getFromStorage('profile_is_completed');

  if (cached === true) {
    return { is_completed: cached };
  }

  try {
    const response = await apiClient.get('api/profile/is-completed');
    const data = response.data;
    setStorage('profile_is_completed', data.is_completed);
    return data;
  } catch (error) {
    console.error('Error fetching profile is-completed:', error);
    throw error;
  }
};

export const hasDocument = async (type, matchId) => {
  try {
    const response = await apiClient.get(`api/documents/exists/${type}/${matchId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching document exists:', error);
    throw error;
  }
};

export const getSubscription = async () => {
  try {
    const response = await apiClient.get('api/subscription');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching subscription:', error);
    throw error;
  }
};

export const getJob = async (jobExternalId) => {
  try {
    const response = await apiClient.get(`api/jobs/${jobExternalId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching job:', error);
    throw error;
  }
};

export const cancelSubscription = async (reason) => {
  try {
    const response = await apiClient.post('api/subscription/cancel', {
      explanation: reason,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error canceling subscription:', error);
    throw error;
  }
};

export const fakeCancelSubscription = async (reason) => {
  try {
    const response = await apiClient.post('api/subscription/cancel/log', {
      explanation: reason,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error recording cancel attempt:', error);
    throw error;
  }
};

export const getMatchedJobs = async (params = {}) => {
  const { page, limit, ...filters } = params;
  const queryParams = buildJobMatchQueryParams({ page, limit, filters });

  try {
    const response = await apiClient.get(`api/job-matches?${queryParams}`);
    return response.data.data; // Returns { data: [...], pagination: {...} }
  } catch (error) {
    console.error('Error fetching matched jobs:', error);
    throw error;
  }
};

export const getSavedJobs = async (params = {}) => {
  const { page, limit, ...filters } = params;
  const queryParams = buildJobMatchQueryParams({ page, limit, filters });

  try {
    const response = await apiClient.get(`api/job-matches/saved?${queryParams}`);
    return response.data.data; // Returns { data: [...], pagination: {...} }
  } catch (error) {
    console.error('Error fetching matched jobs:', error);
    throw error;
  }
};

export const getSearchJobs = async (params = {}) => {
  const { page, limit, query, ...filters } = params;
  const queryParams = buildJobMatchQueryParams({ page, limit, filters, query });

  try {
    const response = await apiClient.get(`api/jobs/search?${queryParams}`);

    return response.data.data; // Returns { data: [...], pagination: {...} }
  } catch (error) {
    console.error('Error fetching matched jobs:', error);
    throw error;
  }
};

export const getJobsForBoard = async () => {
  try {
    const response = await apiClient.get(`api/job-matches/board`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching matched jobs:', error);
    throw error;
  }
};

export const getDashboardMatchedJobs = async () => {
  try {
    const response = await apiClient.get('api/job-matches/dashboard');
    const data = response.data.data;
    return data;
  } catch (error) {
    console.error('Error fetching dashboard matched jobs:', error);
    throw error;
  }
};

/** Whether to show the low-match refinement flow (server-side rules). */
export const showLowMatchesPopup = async () => {
  try {
    const response = await apiClient.get('api/low-matches/show-popup');
    return response.data.showPopup;
  } catch (error) {
    console.error('Error fetching low matches show-popup:', error);
    throw error;
  }
};

export const getLowMatchesTitles = async () => {
  try {
    const response = await apiClient.get('api/low-matches/titles');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching low matches titles:', error);
    throw error;
  }
};

export const submitLowMatchTitles = async ({ similarTitles, synonymTitles, customTitles }) => {
  try {
    const response = await apiClient.post('api/low-matches/titles/fetch', {
      similarTitles,
      synonymTitles,
      customTitles,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error submitting low match titles:', error);
    throw error;
  }
};

export const getLowMatchesCount = async ({ batchId }) => {
  try {
    const response = await apiClient.get('api/low-matches/matches-count', {
      params: batchId ? { batchId } : {},
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching low matches count:', error);
    throw error;
  }
};

export const getJobStatus = async ({ jobId }) => {
  try {
    const response = await apiClient.get(`api/laravel-jobs/status/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching job status:', error);
    throw error;
  }
};

export const getLowMatchesWorkTypes = async () => {
  try {
    const response = await apiClient.get('api/low-matches/work-types');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching low matches work types:', error);
    throw error;
  }
};

export const submitLowMatchWorkTypes = async ({ jobTypes, employmentTypes }) => {
  try {
    const response = await apiClient.post('api/low-matches/work-types/fetch', {
      jobTypes,
      employmentTypes,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error submitting low match work types:', error);
    throw error;
  }
};

export const updateJobMatchStatus = async ({ matchId, status }) => {
  matchId = parseInt(matchId);

  try {
    const response = await apiClient.post('api/job-matches/status', {
      matchId,
      status,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating job match status:', error);
    throw error;
  }
};

export const saveJob = async ({ jobId }) => {
  try {
    const response = await apiClient.post('api/jobs/save', {
      jobId: jobId,
    });
    return response.data.data;
  } catch (error) {
    console.error('Error saving job:', error);
    throw error;
  }
};

export const applyJob = async (matchId) => {
  try {
    const response = await apiClient.post('api/apply', {
      matchId: matchId,
    });
    return response.data;
  } catch (error) {
    console.error('Error applying job:', error);
    throw error;
  }
};

export const checkApplyDailyLimit = async () => {
  try {
    const response = await apiClient.get('api/apply/daily-limit-status');
    return response.data?.data?.valid === true;
  } catch (error) {
    console.error('Error checking apply daily limit status:', error);
    throw error;
  }
};

export const getApplications = async ({ page = 1, limit = 10 } = {}) => {
  try {
    const response = await apiClient.get('api/apply', {
      params: { page, limit },
    });
    const { data, total, currentPage, perPage } = response.data;
    const lastPage = Math.max(1, Math.ceil(total / perPage));

    return {
      data,
      total,
      page: currentPage,
      limit: perPage,
      lastPage,
    };
  } catch (error) {
    console.error('Error fetching applications:', error);
    throw error;
  }
};

export const getInboxChats = async () => {
  try {
    const response = await apiClient.get('api/inbox');
    const sortedData = response.data.data.sort((a, b) => {
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    });
    return { ...response, data: sortedData };
  } catch (error) {
    console.error('Error fetching inbox messages:', error);
    throw error;
  }
};

export const getInboxChat = async (chatId) => {
  try {
    const response = await apiClient.get(`api/inbox/${chatId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching inbox messages:', error);
    throw error;
  }
};

export const sendInboxChatMessage = async (chatId, message) => {
  try {
    const response = await apiClient.post(`api/inbox/message`, {
      chatId: chatId,
      message: message,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending inbox chat message:', error);
    throw error;
  }
};

export const getApplication = async (applicationId) => {
  try {
    const response = await apiClient.get(`api/apply/${applicationId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching application:', error);
    throw error;
  }
};

export const updateApplicationData = async (applicationId, data) => {
  try {
    const response = await apiClient.post(`api/apply/update-data`, {
      id: applicationId,
      data: data,
    });
    return response.status;
  } catch (error) {
    console.error('Error updating application data:', error);
    throw error;
  }
};

export const updateApplicationFormData = async (applicationId, data) => {
  try {
    const response = await apiClient.post(`api/apply/update-form-data`, {
      id: applicationId,
      data: data,
    });
    return response.status;
  } catch (error) {
    console.error('Error updating application data:', error);
    throw error;
  }
};

export const getDocuments = async (profileId) => {
  try {
    const response = await apiClient.get(`api/documents/${profileId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching documents:', error);
    throw error;
  }
};

export const getMatchedJobDocuments = async (matchId) => {
  try {
    const response = await apiClient.get(`api/documents/match/${matchId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching matched job documents:', error);
    throw error;
  }
};

export const getJobMatch = async (matchId) => {
  try {
    const response = await apiClient.get(`api/job-matches/${matchId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching job match:', error);
    throw error;
  }
};

export const getJobBrowse = async (jobId) => {
  try {
    const response = await apiClient.get(`api/jobs/${jobId}`);
    return response.data.data;
  } catch (error) {
    console.error('Error fetching job browse:', error);
    throw error;
  }
};

export const getJobMatchFilters = async () => {
  try {
    const response = await apiClient.get('api/job-matches/filters');
    return response.data;
  } catch (error) {
    console.error('Error fetching job match filters:', error);
    throw error;
  }
};

export const updateClientCvData = async (matchId, profileData) => {
  try {
    const response = await apiClient.post('api/cv/save-data', {
      matchId,
      cvData: profileData,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating CV data:', error);
    throw error;
  }
};

export const downloadResume = async (matchId) => {
  try {
    const response = await apiClient.get(`api/job-matches/cv/${matchId}`, {
      responseType: 'blob',
    });
    return response.data;
  } catch (error) {
    console.error('Error downloading resume:', error);
    throw error;
  }
};

export const updateProfile = async (payload) => {
  try {
    const response = await apiClient.post('api/profile/update', payload);

    const clientId = await handleClientId();
    flushStorage('profile_' + clientId);

    return response.data;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

export const setDesignTypes = async (designTypes) => {
  try {
    const response = await apiClient.post('api/profile/update/resume-design-type', designTypes);
    return response.status;
  } catch (error) {
    console.error('Error updating design types:', error);
    throw error;
  }
};

export const createClientProfile = async (quizAnswers) => {
  try {
    const response = await apiClient.post('api/profile/create', {
      quiz_answers: quizAnswers,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating client profile:', error);
    throw error;
  }
};

export const handleClientId = async () => {
  const clientId = localStorage.getItem('clientId');
  if (clientId) return clientId;

  try {
    const response = await apiClient.get('api/client/id');
    localStorage.setItem('clientId', response.data.id);
    return response.data.id;
  } catch (error) {
    console.error('Error fetching client ID:', error);
    throw error;
  }
};

export const getChatInterview = async (jobId) => {
  try {
    const response = await apiClient.get(`api/interview/${jobId}`);
    if (response.status === 204) {
      return response;
    }
    return response.data;
  } catch (error) {
    console.error('Error fetching chat interview:', error);
    throw error;
  }
};

export const sendMessage = async (interviewId, message) => {
  try {
    const response = await apiClient.post(`api/interview/message/send`, {
      interviewId,
      message,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

export const createInterview = async (jobPostId) => {
  try {
    const response = await apiClient.post(`api/interview/create`, {
      clientJobMatchId: jobPostId,
    });
    return response.data;
  } catch (error) {
    console.error('Error creating interview:', error);
    throw error;
  }
};

export const uploadOnboardingResume = async (file) => {
  try {
    const formData = new FormData();
    formData.append('resume', file);

    const response = await apiClient.post('api/onboarding/upload/resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.status;
  } catch (error) {
    console.error('Error uploading onboarding resume:', error);
    throw error;
  }
};

/** Replace an existing profile resume (authenticated client). Backend must respond with 204. */
export const reuploadProfileResume = async (file) => {
  try {
    const formData = new FormData();
    formData.append('resume', file);

    const response = await apiClient.post('api/profile/reupload/resume', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.status;
  } catch (error) {
    console.error('Error reuploading profile resume:', error);
    throw error;
  }
};

export const downloadDefaultResume = async () => {
  try {
    const response = await apiClient.get('api/file/download/default-resume', {
      responseType: 'blob',
    });
    return response;
  } catch (error) {
    console.error('Error downloading default resume:', error);
    throw error;
  }
};

export const getOnboardingStatus = async () => {
  try {
    const response = await apiClient.get('api/onboarding/resume');
    return response.data;
  } catch (error) {
    console.error('Error fetching onboarding status:', error);
    throw error;
  }
};

export const updateOnboardingStatus = async () => {
  try {
    const response = await apiClient.post('api/onboarding/complete');
    return response.data;
  } catch (error) {
    console.error('Error updating onboarding status:', error);
    throw error;
  }
};

export const getOnboardingProfileStatus = async () => {
  try {
    const response = await apiClient.get('api/onboarding/status');
    return response.data;
  } catch (error) {
    console.error('Error fetching onboarding status:', error);
    throw error;
  }
};

export const generateCoverLetter = async (clientJobMatchId) => {
  try {
    const response = await apiClient.post(`api/cover-letter/generate`, {
      clientJobMatchId: clientJobMatchId,
    });
    return response.status;
  } catch (error) {
    console.error('Error updating application data:', error);
    throw error;
  }
};

export const getCoverLetter = async (clientJobMatchId) => {
  try {
    const response = await apiClient.get(`api/cover-letter/${clientJobMatchId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching cover letter generation status:', error);
    throw error;
  }
};

export const updateCoverLetter = async (clientJobMatchId, content) => {
  try {
    const response = await apiClient.post(`api/cover-letter/update`, {
      clientJobMatchId: clientJobMatchId,
      content: content,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating application data:', error);
    throw error;
  }
};

export const downloadFile = async (clientJobMatchId, type) => {
  try {
    const response = await apiClient.get(`api/file/download/${clientJobMatchId}/${type}`, { responseType: 'blob' });
    return response.data;
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

export const getAssistant = async () => {
  try {
    const response = await apiClient.get('api/assistant');
    return response.data;
  } catch (error) {
    console.error('Error fetching assistant:', error);
    throw error;
  }
};

export const sendAssistantMessage = async (chatId, message) => {
  try {
    const response = await apiClient.post('api/assistant/message', {
      chatId: chatId,
      message: message,
    });
    return response.data;
  } catch (error) {
    console.error('Error sending assistant message:', error);
    throw error;
  }
};

export const customizeResume = async (matchId, addedSkills, certifications, level) => {
  try {
    const response = await apiClient.post('api/cv/customize', {
      matchId,
      hardSkills: addedSkills.hard_skills,
      softSkills: addedSkills.soft_skills,
      certifications: certifications,
      level: level,
    });
    return response.data;
  } catch (error) {
    console.error('Error updating resume by part:', error);
    throw error;
  }
};

export const getLaravelJobStatus = async (jobId) => {
  try {
    const response = await apiClient.get(`api/laravel-jobs/status/${jobId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching resume generation status:', error);
    throw error;
  }
};

export const getSearchSettings = async () => {
  try {
    const response = await apiClient.get('api/profile/search-settings');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching search settings:', error);
    throw error;
  }
};

export const updateSearchSettings = async (payload) => {
  try {
    const response = await apiClient.post('api/profile/update/search-settings', payload);

    return response.data.data;
  } catch (error) {
    console.error('Error updating search settings:', error);
    throw error;
  }
};

export const showReviewPopup = async () => {
  try {
    const response = await apiClient.get('api/reviews/show');
    return response.data.data;
  } catch (error) {
    console.error('Error fetching review popup visibility:', error);
    return false;
  }
};

export const leaveReview = async (rating, comment = null, id = null) => {
  try {
    const response = await apiClient.post('api/reviews', {
      rating,
      comment,
      id,
    });
    return response.data;
  } catch (error) {
    console.error('Error leaving review:', error);
    throw error;
  }
};

export const extendSubscription = async (duration = 1, durationType = 'week') => {
  try {
    const response = await apiClient.post('/api/subscription/extend', {
      duration,
      durationType,
    });
    return response.data;
  } catch (error) {
    console.error('Error extending subscription:', error);
    throw error;
  }
};

/**
 * @typedef {{ background: string, attire: string, vibe: string }} ProfileImageStyleSelection
 */

/**
 * Upload reference photos and start headshot generation. Returns job with `id` and `status`.
 * @param {File[]} files
 * @param {ProfileImageStyleSelection} styles
 */
export const generateProfileImages = async (files, styles) => {
  try {
    const formData = new FormData();
    for (const file of files) {
      formData.append('images[]', file);
    }

    if (styles?.background) {
      formData.append('background', styles.background);
    }
    if (styles?.attire) {
      formData.append('attire', styles.attire);
    }
    if (styles?.vibe) {
      formData.append('vibe', styles.vibe);
    }

    const response = await apiClient.post('api/profile/images/generate', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    const body = response.data?.data ?? response.data;
    return normalizeProfileImageJob(body);
  } catch (error) {
    console.error('Error generating profile images:', error);
    throw error;
  }
};

/** Poll headshot job by id (`queued` | `in_progress` | `completed` | `failed`). */
export const getProfileImageStatus = async (id) => {
  try {
    const response = await apiClient.get(`api/profile/images/${id}`);
    const body = response.data?.data ?? response.data;
    return normalizeProfileImageJob(body);
  } catch (error) {
    console.error('Error fetching profile image status:', error);
    throw error;
  }
};
